import { Link, useLocation } from "react-router-dom";
import { navActivityLabel } from "@fomofi/shared";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface SidebarNavItemProps {
  to: string;
  label: string;
  icon: React.ElementType;
  collapsed: boolean;
  disabled?: boolean;
  /** Lit by the caller when the item stands for several pages (the five-item nav). */
  active?: boolean;
  /** A count beside the label, e.g. Activity's purchases to sort. */
  badge?: number;
}

export default function RedesignNavItem({
  to,
  label,
  icon: Icon,
  collapsed,
  disabled,
  active,
  badge,
}: SidebarNavItemProps) {
  const { pathname } = useLocation();
  const isActive =
    !disabled && (active ?? (pathname === to || (to !== "/" && pathname.startsWith(to))));
  // The cap is visual only; the spoken name carries the exact count, in the shared
  // words (rule 17 with mobile, #1988): "Activity, 12 to sort". Only Activity has a badge.
  const badgeText = badge && badge > 0 ? (badge > 99 ? "99+" : String(badge)) : null;
  const spoken = badge && badge > 0 ? navActivityLabel(badge) : label;

  const item = disabled ? (
    <span
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium cursor-not-allowed opacity-40",
        collapsed ? "justify-center px-0" : "",
      )}
    >
      <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} />
      {!collapsed && <span>{label}</span>}
    </span>
  ) : (
    <Link
      to={to}
      aria-current={isActive ? "page" : undefined}
      // Collapsed, the link is an icon and nothing else, and the tooltip below
      // is not an accessible name: it opens on hover and focus, and assistive
      // technology reads the link before either happens. So the sweep found
      // twelve links with no name at all on every route measured with the
      // sidebar collapsed (BEN-141 QF11, `a11y.unlabeled-control`). Expanded,
      // the label is the link's own text and a duplicate aria-label would only
      // override it with the same words.
      aria-label={collapsed ? spoken : undefined}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150",
        collapsed ? "justify-center px-0" : "",
        isActive
          ? "bg-sidebar-primary text-sidebar-primary-foreground"
          : "text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground",
      )}
    >
      <span className="relative shrink-0">
        <Icon className="h-[18px] w-[18px]" strokeWidth={isActive ? 2 : 1.75} />
        {collapsed && badgeText && (
          <span
            aria-hidden="true"
            className="absolute -right-1.5 -top-1.5 h-2.5 w-2.5 rounded-full bg-warning ring-2 ring-sidebar"
          />
        )}
      </span>
      {!collapsed && <span className="flex-1">{label}</span>}
      {!collapsed && badgeText && (
        <span
          aria-hidden="true"
          className="flex h-5 min-w-5 items-center justify-center rounded-full bg-warning px-1.5 text-[11px] font-bold tabular-nums text-warning-foreground"
        >
          {badgeText}
        </span>
      )}
      {!collapsed && badgeText && <span className="sr-only">, {badge} to sort</span>}
    </Link>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{item}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          {label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return item;
}
